import { Component, OnDestroy } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { AuthService } from '../auth.service';
import { Observable, BehaviorSubject, fromEvent, merge, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';
import localforage from 'localforage';
import { SearchCountryField, CountryISO, PhoneNumberFormat, NgxIntlTelInputModule } from 'ngx-intl-tel-input';
import { isEmpty } from 'lodash-es'
import { capitalize } from '../../../globals/functions';
import { AlertComponent } from 'ngx-bootstrap/alert';
import { NgOptimizedImage, AsyncPipe } from '@angular/common';
import { FlexModule } from '@angular/flex-layout/flex';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatIcon } from '@angular/material/icon';


@Component({
    selector: 'app-sign-up',
    templateUrl: './sign-up.component.html',
    styleUrls: ['./sign-up.component.scss'],
    imports: [
        NgOptimizedImage,
        FormsModule,
        ReactiveFormsModule,
        NgxIntlTelInputModule,
        FlexModule,
        MatProgressSpinner,
        MatIcon,
        RouterLink,
        RouterLinkActive,
        AsyncPipe,
    ],
})
export class SignUpComponent implements OnDestroy {
  isComingFromFailedSignIn = false;
  private trialWebsite: string = null;
  private isAutoSignup = false;
  separateDialCode = false;
	SearchCountryField = SearchCountryField;
	CountryISO = CountryISO;
  PhoneNumberFormat = PhoneNumberFormat;
	preferredCountries: CountryISO[] = [CountryISO.India, CountryISO.UnitedStates];
  isOnline: boolean = true;


  signUpForm = new UntypedFormGroup({
		sms: new UntypedFormControl(undefined, [Validators.required]),
    fullName: new UntypedFormControl(undefined, [Validators.required]),
    email: new UntypedFormControl(undefined, [Validators.required, Validators.email])
	});


  // sms = new FormControl('');
  // fullName = new FormControl('');
  // email = new FormControl('');

  private busy_ = new BehaviorSubject(false);
  public busy = this.busy_.asObservable();

  private errorMessage_ = new BehaviorSubject('');
  public errorMessage = this.errorMessage_.asObservable();
  private onlineStatusSubscription?: Subscription;
  private queryParamsSubscription?: Subscription;

  constructor(private router: Router, private auth: AuthService, private route: ActivatedRoute) { }

  createOnline$() {
    return merge(
      fromEvent(window, 'offline').pipe(map(() => false)),
      fromEvent(window, 'online').pipe(map(() => true)),
      new Observable<boolean>((sub) => {
        sub.next(navigator.onLine);
        sub.complete();
      }));
  }

  ngOnInit(): void {
    this.onlineStatusSubscription = this.createOnline$().subscribe(isOnline => this.isOnline = isOnline);
    this.queryParamsSubscription = this.route.queryParams.subscribe(params => {
      console.log("Query Params are :", params)
      if(params && ! isEmpty(params)){
        if (params['name'] && params['email'] && params['phone']) {
          // Trial redirect from marketing site — bypass form, call auth directly
          this.trialWebsite = params['website'] || null;
          this.isAutoSignup = true;
          this.autoTrialSignup(params['name'], params['email'], params['phone']);
        } else {
          this.signUpForm.controls['sms'].setValue(params);
          this.isComingFromFailedSignIn = true;
        }
      }
    });

    localforage.getItem('lastSignUpData').then(event => {
      if(event && event != null && !this.isAutoSignup){
        this.signUpForm.controls['sms'].setValue((<any>event).sms);
        this.signUpForm.controls['fullName'].setValue((<any>event).fullName);
        this.signUpForm.controls['email'].setValue((<any>event).email);
      }
    })
      .catch(e => {
        console.log('Error in fetching saved lastSignUpData', e);
      });
  }

  ngOnDestroy(): void {
    this.onlineStatusSubscription?.unsubscribe();
    this.queryParamsSubscription?.unsubscribe();
  }

  /** Auto-signup from marketing site redirect — bypasses form controls entirely */
  private async autoTrialSignup(name: string, email: string, phone: string) {
    this.errorMessage_.next('');
    this.busy_.next(true);
    try {
      let capitalizedName = capitalize(name);
      let isNewUser = false;
      try {
        await this.auth.signUp(phone, capitalizedName, email as any, '12345', 'superadmin');
        isNewUser = true;
      } catch (signUpErr) {
        // User already exists — just sign them in, skip workspace setup
        console.log("Sign-up skipped (user already exists):", signUpErr?.message || signUpErr);
      }
      await this.auth.signIn(phone);
      let otpQueryParams: any = { number: phone };
      // Only route to workspace setup for brand new users
      if (isNewUser && this.trialWebsite) otpQueryParams.website = this.trialWebsite;
      this.router.navigate(['/enter-secret-code'], { queryParams: otpQueryParams });
    } catch (err) {
      console.log("Error in auto trial sign-up", err);
      this.errorMessage_.next(err.message || err);
    } finally {
      this.busy_.next(false);
    }
  }

  public async signup() {
    this.errorMessage_.next('');
    this.busy_.next(true);
    let e164Number = this.signUpForm.controls['sms'].value.e164Number;
    try {
      let name = capitalize(this.signUpForm.controls['fullName'].value);
      await this.auth.signUp(e164Number, name, this.signUpForm.controls['email'].value, '12345', 'superadmin');
      await this.auth.signIn(e164Number);
      let o = {'sms' : this.signUpForm.controls['sms'].value, 'fullName' : name, 'email' : this.signUpForm.controls['email'].value};
      localforage.setItem('lastSignUpData', o);
      let otpQueryParams: any = { number: e164Number };
      if (this.trialWebsite) otpQueryParams.website = this.trialWebsite;
      this.router.navigate(['/enter-secret-code'], { queryParams: otpQueryParams });
    } catch (err) {
      console.log("Error Occured in Sign-Up", err);
      if (err && err.code == "UsernameExistsException"){
        await this.auth.signIn(e164Number);
        localforage.setItem('lastSignInNumber', this.signUpForm.controls['sms'].value);
        let otpQueryParams: any = { number: e164Number };
        if (this.trialWebsite) otpQueryParams.website = this.trialWebsite;
        this.router.navigate(['/enter-secret-code'], { queryParams: otpQueryParams });
      }
      this.errorMessage_.next(err.message || err);
    } finally {
      this.busy_.next(false);
    }
  }
}
